import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ContainerImageInfo,
  ContainerImageSearchResult,
} from '@back4app2/sdk';

import back4app2 from '../../back4app2';
import { trackEvent, AmplitudeEvent } from '../../utils/amplitude';
import Button from '../Button';
import TextInput from '../FormInputField/TextInput/TextInput';
import { DOCKER_LOGO } from '../BuildMethodSelector/stackLogos';
import { ReactComponent as VisibilityIcon } from '../../assets/images/visibility-icon.svg';
import { ReactComponent as VisibilityOffIcon } from '../../assets/images/visibility-off-icon.svg';

// One provider ships today, and it is named in the field hints rather than
// asked for again. The field stays in the value so the SDK call and a second
// provider both have somewhere to land.
export type RegistryProvider = 'DOCKER_HUB';

export interface ContainerImageValue {
  provider: RegistryProvider;
  repository: string;
  tag: string;
  // Credentials, when the user has them. There is deliberately no "is this
  // private?" flag: that asks the user to classify the image before they can
  // supply a token, and getting the classification wrong produced a NOT_FOUND
  // with no obvious way out. What they know is whether they have credentials.
  username: string;
  password: string;
}

export const INITIAL_CONTAINER_IMAGE: ContainerImageValue = {
  provider: 'DOCKER_HUB',
  repository: '',
  tag: 'latest',
  username: '',
  password: '',
};

interface ContainerImageSourceProps {
  value: ContainerImageValue;
  onChange: (value: ContainerImageValue) => void;
  // Raised whenever the resolution settles, so the step can gate Next on a
  // real answer instead of on the fields merely being filled in.
  onResolved: (info: ContainerImageInfo | null) => void;
  onNext: () => void;
}

// Shorter than a search box's debounce: the suggestions sit under the field
// being typed in, so a lag there reads as the list not keeping up.
const SEARCH_DEBOUNCE_MS = 300;

// One and two letters match tens of thousands of repositories and rank by
// nothing useful, so the request is spent on an answer nobody can act on.
// Waiting for the third character removes most of the traffic a search box
// generates without the user ever noticing a delay.
const MIN_SEARCH_LENGTH = 3;

// The platform our nodes run. Stated in the worker and in the API resolver
// too; the dashboard repeats it rather than importing across the boundary.
const REQUIRED_PLATFORM = 'linux/amd64';

const LISTBOX_ID = 'container-image-suggestions';

// One label style on this screen, and the same one the wizard's other forms
// use. `normal-case` matters: TextInput capitalizes labels, which turns
// "Access token" into "Access Token" — against the design system's
// sentence-case rule.
const LABEL_CLASSNAME = 'text-[0.8125rem] font-medium normal-case text-text-dim';
const HINT_CLASSNAME = 'text-xs leading-140 text-text-faint';

// `acme/api:1.4.2` typed or pasted whole into the repository field, split
// into the two values the API takes. Only the LAST colon, and only when it
// comes after the last slash — a colon before it belongs to a registry host's
// port, not to the tag. A bare trailing colon keeps the tag already there.
export function splitReference(
  text: string,
  currentTag: string
): { repository: string; tag: string } {
  const typed = (text || '').trim();
  const separator = typed.lastIndexOf(':');

  if (separator < 0 || separator < typed.lastIndexOf('/')) {
    return { repository: typed, tag: currentTag };
  }

  return {
    repository: typed.slice(0, separator),
    tag: typed.slice(separator + 1) || currentTag,
  };
}

function formatSize(sizeBytes?: number): string | null {
  if (!sizeBytes) {
    return null;
  }

  const mb = sizeBytes / (1024 * 1024);

  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

function formatCount(count: number): string {
  // Billions matter now. This used to format star counts, which top out in
  // the tens of thousands; pull counts for an official image run to eleven
  // figures, and without this rung `nginx` reported "13311.2M pulls" — a
  // number nobody can read at a glance, which is the only thing this column
  // is for.
  if (count >= 1000000000) {
    return `${(count / 1000000000).toFixed(1)}B`;
  }

  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }

  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }

  return `${count}`;
}

// Every one of these is a wrong answer the user can fix right now, in the
// field they are already looking at. Left to the deploy they arrive minutes
// later as a container that never started, blamed on something else.
function messageForError(
  errorCode: string | undefined,
  reference: string
): string {
  switch (errorCode) {
    case 'NOT_FOUND':
      // Also the answer for a private image asked for anonymously: Docker Hub
      // will not say which, so the message names the way out of both.
      return `We couldn't find ${reference} on Docker Hub, or it's private — add credentials below.`;
    case 'UNAUTHORIZED':
      return 'Docker Hub rejected these credentials.';
    case 'INCOMPLETE_CREDENTIALS':
      return 'Add both the username and the access token.';
    case 'RATE_LIMITED':
      return 'Docker Hub rate-limited us. Try again in a few minutes.';
    case 'UNSUPPORTED_PLATFORM':
      return "This image has no linux/amd64 variant — our nodes can't run it.";
    case 'INVALID_REFERENCE':
      return 'Enter an image like "nginx" or "acme/api".';
    case 'BLOCKED_IMAGE':
      // Says nothing about how we recognised it. The worker refuses this same
      // image at deploy whatever it is called, and naming the digest here
      // would just tell someone probing us which property to change.
      return "This image isn't allowed on Back4app Containers. Contact support if you think that's wrong.";
    default:
      return "We couldn't reach Docker Hub. Try again in a moment.";
  }
}

/**
 * One form, in the order the question is asked: which image, which tag, and
 * credentials if it is private.
 *
 * It used to be three places to type the same name — a search box in the
 * panel head, a "Private image, or not listed?" bar under the results, and
 * the Repository field on the screen after — with credentials only reachable
 * on that last screen. Search is now a suggestion list on the Repository
 * field itself: someone who knows the name types it and moves on, someone who
 * does not picks it from the list, and a private image is simply a name the
 * list does not show rather than a separate route.
 */
const ContainerImageSource = ({
  value,
  onChange,
  onResolved,
  onNext,
}: ContainerImageSourceProps) => {
  const { Logo: DockerLogo, color: dockerColor } = DOCKER_LOGO;

  // What the suggestions answer: only text the user typed. Picking a
  // suggestion also writes the repository, and must not search for it again.
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<
    ContainerImageSearchResult[] | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isListOpen, setIsListOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef(0);

  const [showPassword, setShowPassword] = useState(false);
  // Raised on Next, not while typing: a username with no token yet is the
  // normal state of someone halfway through the two fields.
  const [credentialsError, setCredentialsError] = useState<string>();

  // The last answer and "a request is running" are separate state on purpose:
  // editing a field drops the verdict without blanking the line mid-check.
  const [info, setInfo] = useState<ContainerImageInfo | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const { provider, repository, tag, username, password } = value;

  const update = useCallback(
    (patch: Partial<ContainerImageValue>) => {
      // A verdict belongs to the values that produced it, so editing any of
      // them drops it. Leaving a green "Found" under a repository the user
      // has since changed is the worst of both worlds.
      setInfo(null);
      onResolved(null);
      onChange({ ...value, ...patch });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, value]
  );

  // A tag typed into the query is not part of what Docker Hub's index is
  // searched by — `nginx:1.27` should still suggest `nginx`.
  const searchTerm = splitReference(query, '').repository;
  const isSearchable = searchTerm.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!isSearchable) {
      setSuggestions(null);
      setIsSearching(false);

      return;
    }

    let cancelled = false;

    const timeout = setTimeout(async () => {
      const requestId = searchRef.current + 1;
      searchRef.current = requestId;

      setIsSearching(true);

      let found: ContainerImageSearchResult[] = [];

      try {
        found = await back4app2.containerImageSearch(provider, searchTerm);
      } catch (e) {
        found = [];
      }

      if (cancelled || searchRef.current !== requestId) {
        return;
      }

      setSuggestions(found);
      setActiveIndex(-1);
      setIsSearching(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchTerm, isSearchable, provider]);

  const pick = (result: ContainerImageSearchResult) => {
    // Keeps a tag the user already typed; `latest` is only the default.
    update({ repository: result.repository });
    setQuery('');
    setIsListOpen(false);
    setActiveIndex(-1);
  };

  // Splits `repo:tag` out of the repository field once the user is done with
  // it, so the tag lands in its own field where they can see it.
  const splitRepositoryField = () => {
    const split = splitReference(repository, tag);

    if (split.repository !== repository || split.tag !== tag) {
      update(split);
    }
  };

  // Resolved on Next, not while typing. A check per keystroke sends a burst of
  // requests to Docker Hub for answers about half-typed names that nobody
  // asked for — and each one costs a token request plus a manifest read.
  const checkAndContinue = async () => {
    if (Boolean(username) !== Boolean(password)) {
      setCredentialsError('Add both the username and the access token.');

      return;
    }

    // Next can be clicked straight from a repository field still holding
    // `repo:tag`, before its blur has re-rendered the split values.
    const split = splitReference(repository, tag);

    if (split.repository !== repository || split.tag !== tag) {
      onChange({ ...value, ...split });
    }

    const hasCredentials = Boolean(username && password);

    // The Docker Hub counterpart of "Import GitHub Repo": the point where a
    // source stops being browsed and starts being committed to. Reported
    // before the call, not after, so the event counts attempts — a resolve
    // that fails is exactly the case worth counting.
    trackEvent(AmplitudeEvent.IMPORT_DOCKER_HUB_IMAGE_CLICK, {
      provider,
      private: hasCredentials,
      // Only for public images. A private repository name is the customer's,
      // it can carry a company or product nobody has announced, and which
      // public images people reach for is the whole question this property
      // exists to answer — so the half that has product value is also the half
      // that is already public.
      repository: hasCredentials ? undefined : split.repository,
    });

    setIsChecking(true);
    setIsListOpen(false);
    setInfo(null);
    onResolved(null);

    let result: ContainerImageInfo | null = null;

    try {
      result = await back4app2.containerImageInfo(
        provider,
        split.repository,
        split.tag,
        hasCredentials ? username : undefined,
        hasCredentials ? password : undefined
      );
    } catch (e) {
      result = null;
    }

    setIsChecking(false);
    setInfo(result);

    if (result && result.exists && !result.errorCode) {
      onResolved(result);
      onNext();

      return;
    }

    // Everything else is a refusal the user is now looking at. The code is the
    // same one `messageForError` switches on, so the event and the sentence on
    // screen can never disagree — including the fallback: a thrown request and
    // an `exists: false` with no code both render as "we couldn't reach Docker
    // Hub", so both report as UNREACHABLE rather than inventing a distinction
    // the user was never shown.
    trackEvent(AmplitudeEvent.DOCKER_HUB_IMAGE_RESOLVE_ERROR, {
      provider,
      private: hasCredentials,
      errorCode: result?.errorCode || 'UNREACHABLE',
    });
  };

  const showList = isListOpen && isSearchable && (isSearching || !!suggestions);
  const options = suggestions || [];

  const onRepositoryKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsListOpen(false);

      return;
    }

    if (!showList || !options.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? options.length - 1 : index - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      pick(options[activeIndex]);
    }
  };

  const reference = `${repository}:${tag}`;
  const sizeLabel = info ? formatSize(info.sizeBytes) : null;
  const shortDigest =
    info && info.digest ? `${info.digest.slice(0, 19)}…` : null;

  let status;

  if (isChecking) {
    status = <span className="opacity-70">Checking Docker Hub…</span>;
  } else if (info && info.exists && !info.errorCode) {
    // Only the platform we actually run is worth naming. `nginx` ships eight;
    // listing them all answered a question nobody asked.
    const runsHere = info.architectures.some(
      (architecture) =>
        architecture === REQUIRED_PLATFORM ||
        architecture.startsWith(`${REQUIRED_PLATFORM}/`)
    );

    status = (
      <span className="text-cta-green">
        Found
        {runsHere ? ` · ${REQUIRED_PLATFORM}` : ''}
        {sizeLabel ? ` · ${sizeLabel}` : ''}
        {shortDigest ? ` · ${shortDigest}` : ''}
      </span>
    );
  } else if (info) {
    status = (
      <span className="text-error-red">
        {messageForError(info.errorCode, reference)}
      </span>
    );
  } else {
    status = null;
  }

  // Next is what performs the check, so it is enabled whenever there is a
  // coherent question to ask — not once an answer is already in hand.
  const canContinue = Boolean(repository.trim() && tag && !isChecking);

  let listContent;

  if (isSearching && !suggestions) {
    listContent = (
      <div className="px-3.5 py-3 text-xs leading-140 text-text-faint">
        Searching Docker Hub…
      </div>
    );
  } else if (!options.length) {
    listContent = (
      <div className="px-3.5 py-3 text-xs leading-140 text-text-faint">
        No public image matches "{searchTerm}". Private images never appear
        here — type the full name and add credentials below.
      </div>
    );
  } else {
    listContent = options.map((result, index) => (
      <div
        key={result.repository}
        id={`${LISTBOX_ID}-${index}`}
        role="option"
        aria-selected={index === activeIndex}
        // mousedown, not click: a click lands after the input's blur, which
        // has already closed the list.
        onMouseDown={(event) => {
          event.preventDefault();
          pick(result);
        }}
        onMouseEnter={() => setActiveIndex(index)}
        className={`flex items-center gap-3 px-3.5 py-2.5 cursor-pointer ${
          index === activeIndex ? 'bg-navy-700' : ''
        }`}
      >
        <span className="flex-none w-5 flex items-center justify-center text-navy-200">
          <DockerLogo
            width="15"
            height="15"
            fill="currentColor"
            color={dockerColor}
          />
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span className="text-[0.875rem] font-medium leading-140 text-light-blue truncate">
              {result.repository}
            </span>
            {result.isOfficial && (
              <span className="flex-none text-[10px] leading-140 tracking-[0.08em] uppercase px-1.5 py-0.5 rounded bg-old-blue/[0.14] text-light-blue">
                Official
              </span>
            )}
          </span>
          {result.description && (
            <span className="block text-xs leading-140 mt-0.5 text-text-faint truncate">
              {result.description}
            </span>
          )}
        </span>
        {/* Pulls, not stars: how many times the image was actually pulled is
            the closer thing to "is this the one everybody uses". */}
        <span className="flex-none text-xs leading-140 text-text-faint whitespace-nowrap [@media(max-width:639.98px)]:hidden">
          {formatCount(result.pullCount)} pulls
        </span>
      </div>
    ));
  }

  return (
    <div className="px-[1.875rem] py-7 space-y-6">
      <div>
        {/* Repository and tag are one reference — `repo:tag` — so they sit on
            one row. */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_16rem] gap-4">
          <div className="relative">
            <TextInput
              label="Repository"
              labelClassName={LABEL_CLASSNAME}
              name="imageRepository"
              value={repository}
              isRequired
              placeholderText="Search Docker Hub, or type e.g. acme/api"
              onChange={(event) => {
                update({ repository: event.target.value });
                setQuery(event.target.value);
                setIsListOpen(true);
                setActiveIndex(-1);
              }}
              onFocus={() => setIsListOpen(Boolean(query))}
              inputProps={{
                role: 'combobox',
                autoComplete: 'off',
                spellCheck: false,
                'aria-autocomplete': 'list',
                'aria-expanded': showList,
                'aria-controls': LISTBOX_ID,
                'aria-activedescendant':
                  showList && activeIndex >= 0
                    ? `${LISTBOX_ID}-${activeIndex}`
                    : undefined,
                onKeyDown: onRepositoryKeyDown,
                onBlur: () => {
                  setIsListOpen(false);
                  splitRepositoryField();
                },
              }}
            />
            {showList && (
              <div
                id={LISTBOX_ID}
                role="listbox"
                aria-label="Docker Hub images"
                className="absolute left-0 right-0 top-full mt-1 z-20 max-h-72 overflow-y-auto rounded-control bg-navy-800 border border-navy-400 shadow-lg"
              >
                {listContent}
              </div>
            )}
          </div>
          <TextInput
            label="Tag"
            labelClassName={LABEL_CLASSNAME}
            name="imageTag"
            value={tag}
            isRequired
            placeholderText="latest"
            onChange={(event) => update({ tag: event.target.value })}
          />
        </div>

        <div className={`${HINT_CLASSNAME} mt-2`}>
          On Docker Hub. Official images like nginx need no namespace.
        </div>

        {/* Height reserved whether or not there is something to say, so
            nothing below shifts when the answer lands. */}
        <div className={`${HINT_CLASSNAME} min-h-[1.125rem] mt-2`}>
          {status}
        </div>
      </div>

      {/* Always here, never behind a switch. The old Yes/No asked the user
          to classify the image before they could type a token, and a wrong
          guess came back as an unexplained NOT_FOUND. Two fields rather than
          one `username:token` box: the username stays readable while the
          token is masked, and nobody has to learn a format to fill it in. */}
      <div className="space-y-3">
        <div>
          <div className={`${LABEL_CLASSNAME} leading-140`}>
            Credentials (optional)
          </div>
          <div className={`${HINT_CLASSNAME} mt-1`}>
            Required for private images. Stored encrypted, never returned by
            the API, and never leaves our build nodes.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput
            label="Username"
            labelClassName={LABEL_CLASSNAME}
            name="registryUsername"
            value={username}
            placeholderText="Docker Hub username"
            isError={Boolean(credentialsError && !username)}
            errorMessage={credentialsError}
            inputProps={{ autoComplete: 'off', spellCheck: false }}
            onChange={(event) => {
              setCredentialsError(undefined);
              update({ username: event.target.value.trim() });
            }}
          />
          <TextInput
            label="Access token"
            labelClassName={LABEL_CLASSNAME}
            type={showPassword ? 'text' : 'password'}
            name="registryToken"
            value={password}
            placeholderText="dckr_pat_…"
            isError={Boolean(credentialsError && !password)}
            errorMessage={credentialsError}
            // Off, not `current-password`: the browser would offer this
            // user's Back4app password, which is never the right answer here.
            inputProps={{ autoComplete: 'off' }}
            endAdornment={
              <button
                type="button"
                aria-label={showPassword ? 'Hide token' : 'Show token'}
                className="text-light-blue"
                onClick={() => setShowPassword((shown) => !shown)}
              >
                {showPassword ? (
                  <VisibilityOffIcon width="18" height="18" />
                ) : (
                  <VisibilityIcon width="18" height="18" />
                )}
              </button>
            }
            onChange={(event) => {
              setCredentialsError(undefined);
              update({ password: event.target.value });
            }}
          />
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button
          type="primary"
          isDisabled={!canContinue}
          onClick={checkAndContinue}
        >
          {isChecking ? 'Checking…' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default ContainerImageSource;
